import { useState } from "react";
import { ICluster } from "./user-details";

interface ClusterModalProps {
  clusters: ICluster[];
  loading: boolean;
  error: any;
  onSelect: (cluster: ICluster) => void;
}

export default function ClusterSelectionModal({
  clusters,
  loading,
  error,
  onSelect,
}: ClusterModalProps) {
  const [selected, setSelected] = useState<ICluster | null>(null);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cluster = clusters.find((c) => c.id === e.target.value) ?? null;
    setSelected(cluster);
  };

  const isKanawa = selected?.name === "Kanawa";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 14, 15, 0.72)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "1.5rem",
          boxShadow: "0 32px 80px rgba(15,46,26,0.22)",
          padding: "2.5rem",
          width: "100%",
          maxWidth: "480px",
          border: "1px solid rgba(27,77,46,0.08)",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              background: "rgba(76,175,120,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem",
            }}
          >
            <svg
              width="22"
              height="22"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#1B4D2E"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h2
            style={{
              fontSize: "1.375rem",
              fontWeight: 700,
              color: "rgb(2, 12, 37)",
              marginBottom: "0.375rem",
              fontFamily: "Georgia, serif",
            }}
          >
            Select Your Market Cluster
          </h2>
          <p
            style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.6 }}
          >
            Choose the pharmaceutical cluster your business operates in. This
            determines your registration portal.
          </p>
        </div>

        {/* Dropdown */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label
            style={{
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "rgb(2, 12, 37)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.5rem",
            }}
          >
            Market Cluster <span style={{ color: "#ef4444" }}>*</span>
          </label>

          <div style={{ position: "relative" }}>
            <select
              value={selected?.id ?? ""}
              onChange={handleChange}
              onFocus={() => setIsDropdownFocused(true)}
              onBlur={() => setIsDropdownFocused(false)}
              disabled={loading}
              style={{
                width: "100%",
                padding: "0.8rem 2.5rem 0.8rem 1rem",
                borderRadius: "0.75rem",
                border: isDropdownFocused
                  ? "2px solid rgb(2, 12, 37)"
                  : selected
                    ? "2px solid #4CAF78"
                    : "2px solid rgba(27,77,46,0.15)",
                background: loading ? "#f9fafb" : "#F7FAF8",
                fontSize: "0.875rem",
                color: selected ? "rgb(2, 12, 37)" : "#9ca3af",
                appearance: "none",
                cursor: loading ? "not-allowed" : "pointer",
                outline: "none",
                transition: "border 0.18s ease",
              }}
            >
              <option value="" disabled>
                {loading ? "Loading clusters..." : "Select a market cluster"}
              </option>
              {clusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.name}
                  {cluster.name === "Kanawa" ? " — Dedicated Portal" : ""}
                </option>
              ))}
            </select>

            {/* Custom chevron */}
            <div
              style={{
                position: "absolute",
                right: "0.875rem",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#6b7280",
              }}
            >
              {loading ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ animation: "spin 0.8s linear infinite" }}
                >
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </svg>
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              )}
            </div>
          </div>

          {/* Error state */}
          {error && !loading && (
            <p
              style={{
                fontSize: "0.75rem",
                color: "#dc2626",
                marginTop: "0.4rem",
              }}
            >
              ⚠ Unable to load clusters. Please refresh the page to try again.
            </p>
          )}
        </div>

        {/* Kanawa info hint */}
        {isKanawa && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.6rem",
              background: "rgba(76,175,120,0.08)",
              border: "1px solid rgba(76,175,120,0.25)",
              borderRadius: "0.75rem",
              padding: "0.75rem 1rem",
              marginBottom: "1.25rem",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1B4D2E"
              strokeWidth={2}
              style={{ flexShrink: 0, marginTop: "1px" }}
            >
              <circle cx="12" cy="12" r="10" />
              <path strokeLinecap="round" d="M12 8v4M12 16h.01" />
            </svg>
            <p
              style={{
                fontSize: "0.8rem",
                color: "#1B4D2E",
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              You will be redirected to the{" "}
              <strong>Kanawa dedicated registration portal</strong> to complete
              your sign-up.
            </p>
          </div>
        )}

        {/* Confirm button */}
        <button
          type="button"
          disabled={!selected || loading}
          onClick={() => selected && onSelect(selected)}
          style={{
            width: "100%",
            padding: "0.875rem",
            borderRadius: "0.75rem",
            background: selected && !loading ? "rgb(2, 12, 37)" : "#e5e7eb",
            color: selected && !loading ? "#fff" : "#9ca3af",
            fontWeight: 700,
            fontSize: "0.875rem",
            border: "none",
            cursor: selected && !loading ? "pointer" : "not-allowed",
            transition: "background 0.2s ease",
          }}
        >
          {!selected
            ? "Select a cluster to continue"
            : isKanawa
              ? "Go to Kanawa Portal →"
              : `Continue with ${selected.name} →`}
        </button>

        <p
          style={{
            textAlign: "center",
            fontSize: "0.7rem",
            color: "#d1d5db",
            marginTop: "0.875rem",
          }}
        >
          You must select a cluster to proceed with registration
        </p>
      </div>
    </div>
  );
}
